/**
 * RequestApprovalModal — Modal para solicitar la aprobación de una OC.
 * Muestra un selector de aprobador (usuarios con permission purchases.orders.approve)
 * y dispara la mutation useRequestPurchaseOrderApproval.
 */
import { useMemo, useState } from 'react'

import { Autocomplete, type AutocompleteItem } from '@/components/Autocomplete'
import { ModalCancelButton } from '@/components/ModalActions'
import { Modal } from '@/components/ds/Modal'
import { inputCls, labelCls } from '@/lib/formStyles'
import { useOrderApprovers } from '../hooks/usePurchases'

interface Props {
  open: boolean
  orderNumber: string
  onClose: () => void
  onConfirm: (approverId: string) => void
  isPending?: boolean
}

export function RequestApprovalModal({
  open, orderNumber, onClose, onConfirm, isPending = false,
}: Props) {
  const [approverId, setApproverId] = useState<string>('')
  const { data, isLoading } = useOrderApprovers()

  const items = useMemo<AutocompleteItem[]>(
    () => (data?.approvers ?? []).map(u => ({ id: u.id, label: `${u.full_name} (${u.email})` })),
    [data],
  )

  const handleClose = () => {
    setApproverId('')
    onClose()
  }

  const handleConfirm = () => {
    if (!approverId) return
    onConfirm(approverId)
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`Solicitar aprobación — OC ${orderNumber}`}
      size="sm"
      actions={
        <>
          <ModalCancelButton onClick={handleClose} />
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!approverId || isPending}
            className="px-4 py-2 text-sm font-medium text-on-primary bg-primary hover:bg-primary-hover rounded-lg disabled:opacity-60"
          >
            {isPending ? 'Enviando…' : 'Solicitar aprobación'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-content-muted">
          El aprobador recibirá una notificación con el detalle de la OC y podrá
          aprobarla o anularla desde su bandeja.
        </p>
        <div>
          <label className={labelCls}>Aprobador *</label>
          {isLoading ? (
            <p className="text-xs text-content-subtle">Cargando aprobadores…</p>
          ) : items.length === 0 ? (
            <p className="text-xs text-warning-content-soft">
              No hay usuarios con permiso para aprobar OC en este tenant. Crea
              un rol con la acción <code>purchases.orders.approve</code> y
              asígnalo a un usuario.
            </p>
          ) : (
            <Autocomplete aria-label="Aprobador"
              items={items}
              value={approverId}
              displayValue={items.find(i => i.id === approverId)?.label ?? ''}
              onChange={() => { /* selección vía onSelect */ }}
              onSelect={(item: AutocompleteItem) => setApproverId(item.id)}
              placeholder="Seleccionar aprobador…"
              className={inputCls}
            />
          )}
        </div>
      </div>
    </Modal>
  )
}
